/**
 * Wraps `callback` so that multiple invocations within the same animation
 * frame coalesce into a single call executed via `requestAnimationFrame`.
 * When called repeatedly before the frame fires, the most recent arguments
 * are used. Returns the throttled function and a `cancel` helper to cancel
 * any pending frame (typically called from an effect cleanup).
 *
 * Useful for high-frequency events (scroll, ResizeObserver, etc.) where work
 * should run at most once per frame to avoid layout thrash.
 */
const throttleToAnimationFrames = <TArgs extends unknown[]>(
  callback: (...args: TArgs) => void
): { throttled: (...args: TArgs) => void; cancel: () => void } => {
  // `frameId` and `pendingArgs` are captured by the closures returned below,
  // giving each call to `throttleToAnimationFrames` its own private state.
  let frameId: number | null = null;
  let pendingArgs: TArgs | null = null;

  const throttled = (...args: TArgs) => {
    pendingArgs = args;
    if (frameId !== null) return;
    frameId = requestAnimationFrame(() => {
      frameId = null;
      const argsToUse = pendingArgs!;
      pendingArgs = null;
      callback(...argsToUse);
    });
  };

  const cancel = () => {
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    pendingArgs = null;
  };

  return { throttled, cancel };
};

export default throttleToAnimationFrames;
